import React, { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import {
  ActionBtn,
  AmountCell,
  BulkActions,
  BulkBtn,
  Checkbox,
  EmptyCta,
  EmptyIcon,
  EmptyState,
  EmptySub,
  EmptyTitle,
  FilterSelect,
  MainHeader,
  MainContainer,
  MainHeading,
  MainSubHeading,
  MarketplaceBadge,
  PageButton,
  PageButtons,
  PageInfo,
  PaginationWrap,
  ProductCell,
  ProductId,
  ProductName,
  ProductThumb,
  SearchInput,
  SearchWrap,
  StatusBadge,
  StatusDot,
  SummaryBar,
  SummaryCard,
  SummaryLabel,
  SummarySub,
  SummaryValue,
  SupplierCell,
  SupplierName,
  TableContainer,
  TableRoot,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  TrackStep,
  TrackingCell,
  TrackingNum,
  ActionGroup,
} from "./styles";
import { useGetOrderQuery, useUpdateOrderMutation, orderApi } from "api/orderApi";
import { useDispatch } from "react-redux";
import Loader from "shared/Loader";
import { useNavigate } from "react-router-dom";
import { downloadCsv } from "utils/exportCsv";

const STATUS_API_MAP = {
  fulfilled: "FULL_FILLED,DELIVERED",
  transit: "SHIPPED",
  processing: "PROCESSING,IN_PROGRESS,PENDING",
  payment_pending: "PAYMENT_PENDING",
  disputed: "CANCELED",
};

const MARKETPLACE_API_MAP = {
  shopify: "SHOPIFY",
  ebay: "EBAY",
  amazon: "AMAZON",
  tiktokshop: "TIKTOK",
  walmart: "WALMART",
};

const formatDateParam = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getPeriodStartDate = (periodFilter) => {
  if (periodFilter === "all") return undefined;
  const days = periodFilter === "30d" ? 30 : periodFilter === "3m" ? 90 : 180;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - days);
  return formatDateParam(start);
};

const OrdersDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [updateOrder, { isLoading: isUpdating }] = useUpdateOrderMutation();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [marketplaceFilter, setMarketplaceFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("30d");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, marketplaceFilter, periodFilter]);

  const queryArgs = useMemo(
    () => ({
      page: currentPage,
      limit: pageSize,
      sortBy: "order.orderNumber",
      sortOrder: "DESC",
      searchFilter: debouncedSearch || undefined,
      startDate: getPeriodStartDate(periodFilter),
      status: statusFilter ? STATUS_API_MAP[statusFilter] : undefined,
      marketplace: marketplaceFilter
        ? MARKETPLACE_API_MAP[marketplaceFilter] || marketplaceFilter.toUpperCase()
        : undefined,
    }),
    [currentPage, debouncedSearch, statusFilter, marketplaceFilter, periodFilter]
  );

  const { data: ordersList, refetch, isLoading, isFetching } = useGetOrderQuery(queryArgs);
  const loading = isLoading || isFetching || exporting;
  const allOrders = useMemo(() => ordersList?.results || [], [ordersList]);
  const totalCount = Number(ordersList?.total || 0);
  const summary = useMemo(
    () => ({
      total: Number(ordersList?.stats?.total ?? 0),
      fulfilled: Number(ordersList?.stats?.fulfilled ?? 0),
      transit: Number(ordersList?.stats?.transit ?? 0),
      processing: Number(ordersList?.stats?.processing ?? 0),
      paymentPending: Number(ordersList?.stats?.paymentPending ?? 0),
      disputed: Number(ordersList?.stats?.disputed ?? 0),
      fulfillmentRate: Number(ordersList?.stats?.fulfillmentRate ?? 0),
    }),
    [ordersList]
  );

  const handleCancelOrder = async (orderId, currentStatus) => {
    if (currentStatus === "DELIVERED") {
      message.error("Cannot cancel a delivered order");
      return;
    }

    if (!orderId) {
      message.error("Order ID is missing");
      return;
    }

    try {
      const result = await updateOrder({ orderId, status: "CANCELED" });
      if (result?.data) {
        message.success("Order cancelled successfully");
        refetch();
      } else if (result?.error) {
        message.error(result?.error?.data?.message || "Failed to cancel order");
      }
    } catch (error) {
      message.error("Error cancelling order");
      console.error(error);
    }
  };

  const getStatusLabel = (status) => {
    if (status === "PAYMENT_PENDING") return "Payment Pending";
    if (status === "FULL_FILLED" || status === "DELIVERED") return "Fulfilled";
    if (status === "SHIPPED") return "In Transit";
    if (status === "PROCESSING" || status === "IN_PROGRESS" || status === "PENDING")
      return "Processing";
    if (status === "CANCELED") return "Disputed";
    return "Processing";
  };

  const getStatusTheme = useCallback((status) => {
    const label = getStatusLabel(status);
    if (label === "Payment Pending")
      return { bg: "#fce7f3", color: "#be185d", dot: "#be185d", key: "payment_pending" };
    if (label === "Fulfilled")
      return { bg: "#dcfce7", color: "#16a34a", dot: "#16a34a", key: "fulfilled" };
    if (label === "In Transit")
      return { bg: "#dbeafe", color: "#1d4ed8", dot: "#1d4ed8", key: "transit" };
    if (label === "Disputed")
      return { bg: "#fee2e2", color: "#dc2626", dot: "#dc2626", key: "disputed" };
    return { bg: "#fef3c7", color: "#d97706", dot: "#d97706", key: "processing" };
  }, []);

  const getMarketplace = (order) =>
    (order?.marketplace || "UNKNOWN").toString().toUpperCase().replaceAll("_", " ");
  const getMarketplaceKey = useCallback(
    (order) => getMarketplace(order).toLowerCase().replace(/\s+/g, ""),
    []
  );

  const mappedOrders = useMemo(
    () =>
      allOrders.map((order) => {
        const firstItem = order?.orderItems?.[0];
        const product = firstItem?.product;
        const statusTheme = getStatusTheme(order?.status);
        const productName = product?.name || "Product";
        const quantity =
          order?.orderItems?.reduce((sum, item) => sum + Number(item?.quantity || 0), 0) || 0;
        const owner = product?.owner;
        const supplierName = owner
          ? `${owner.firstName || ""} ${owner.lastName || ""}`.trim() || "Supplier"
          : "—";
        return {
          id: order?.id,
          created: order?.created,
          amount: Number(order?.amount || 0),
          taxRate: order?.taxRate,
          status: order?.status,
          statusTheme,
          statusLabel: getStatusLabel(order?.status),
          productName,
          quantity,
          productId:
            order?.orderNumber != null
              ? String(order.orderNumber)
              : order?.orderTrackingId || order?.id?.slice(0, 8) || "-",
          trackingId: order?.orderTrackingId || "",
          carrierTrackingNumber: order?.carrierTrackingNumber || "",
          carrier: order?.carrier || "",
          carrierCode: order?.carrierCode || "",
          trackingUrl: order?.trackingUrl || "",
          shippedAt: order?.shippedAt || null,
          supplierName,
          productEntityId: product?.id || null,
          productImage: product?.images?.[0]?.url,
          marketplace: getMarketplace(order),
          marketplaceKey: getMarketplaceKey(order),
        };
      }),
    [allOrders, getMarketplaceKey, getStatusTheme]
  );

  const handlePayOrder = (order) => {
    navigate("/order_payment", {
      state: {
        existingOrderId: order.id,
        totalAmount: order.amount,
        taxRate: order.taxRate,
        marketplace: order.marketplaceKey?.toUpperCase() || "SHOPIFY",
      },
    });
  };

  const allVisibleSelected =
    mappedOrders.length > 0 && mappedOrders.every((o) => selectedIds.includes(o.id));
  const someVisibleSelected =
    mappedOrders.some((o) => selectedIds.includes(o.id)) && !allVisibleSelected;

  const toggleAllCurrentPage = (checked) => {
    const ids = mappedOrders.map((o) => o.id).filter(Boolean);
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
    } else {
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
    }
  };

  const toggleOne = (id, checked) => {
    if (!id) return;
    setSelectedIds((prev) => (checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)));
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const mapOrderForExport = (order) => {
    const firstItem = order?.orderItems?.[0];
    const product = firstItem?.product;
    const owner = product?.owner;
    const quantity =
      order?.orderItems?.reduce((sum, item) => sum + Number(item?.quantity || 0), 0) || 0;
    return {
      productId:
        order?.orderNumber != null
          ? String(order.orderNumber)
          : order?.orderTrackingId || order?.id?.slice(0, 8) || "-",
      productName: product?.name || "Product",
      marketplace: getMarketplace(order),
      created: order?.created,
      supplierName: owner
        ? `${owner.firstName || ""} ${owner.lastName || ""}`.trim() || "Supplier"
        : "—",
      trackingId: order?.orderTrackingId || "",
      carrier: order?.carrier || "",
      carrierTrackingNumber: order?.carrierTrackingNumber || "",
      statusLabel: getStatusLabel(order?.status),
      quantity,
      amount: Number(order?.amount || 0),
    };
  };

  const handleExportCsv = async () => {
    if (totalCount === 0) {
      message.warning("No orders to export. Adjust your filters or place an order first.");
      return;
    }

    setExporting(true);
    try {
      const request = dispatch(
        orderApi.endpoints.getOrder.initiate(
          {
            ...queryArgs,
            page: 1,
            limit: Math.max(totalCount, pageSize),
          },
          { forceRefetch: true }
        )
      );
      const exportResult = await request;
      request.unsubscribe();

      const exportOrders = (exportResult?.data?.results || []).map(mapOrderForExport);
      if (exportOrders.length === 0) {
        message.warning("No orders to export. Adjust your filters or place an order first.");
        return;
      }

      const headers = [
        "Order ID",
        "Product",
        "Marketplace",
        "Created At",
        "Supplier",
        "Order Reference",
        "Carrier",
        "Tracking Number",
        "Status",
        "Quantity",
        "Amount (USD)",
      ];

      const rows = exportOrders.map((order) => [
        order.productId,
        order.productName,
        order.marketplace,
        order.created
          ? new Date(order.created).toLocaleString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
          : "",
        order.supplierName,
        order.trackingId,
        order.carrier || "",
        order.carrierTrackingNumber || "",
        order.statusLabel,
        order.quantity,
        order.amount.toFixed(2),
      ]);

      const dateStamp = new Date().toISOString().slice(0, 10);
      downloadCsv(`my-orders-${dateStamp}.csv`, headers, rows);
      message.success(`Exported ${exportOrders.length} order(s) to CSV.`);
    } catch (error) {
      console.error(error);
      message.error("Failed to export orders");
    } finally {
      setExporting(false);
    }
  };

  const paginationPages = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    start = Math.max(1, end - 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  return (
    <MainContainer>
      <MainHeader>
        <MainHeading>My Orders</MainHeading>
        <MainSubHeading>
          Check the status, tracking and management of all your orders across every marketplace.
        </MainSubHeading>
      </MainHeader>

      <SummaryBar>
        <SummaryCard>
          <span className="top orange" />
          <SummaryLabel>Total Orders</SummaryLabel>
          <SummaryValue>{summary.total}</SummaryValue>
          <SummarySub>Selected period</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <span className="top green" />
          <SummaryLabel>Fulfilled</SummaryLabel>
          <SummaryValue $color="#16a34a">{summary.fulfilled}</SummaryValue>
          <SummarySub>{summary.fulfillmentRate}% fulfillment rate</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <span className="top blue" />
          <SummaryLabel>In Transit</SummaryLabel>
          <SummaryValue $color="#1d4ed8">{summary.transit}</SummaryValue>
          <SummarySub>Tracking active</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <span className="top amber" />
          <SummaryLabel>Processing</SummaryLabel>
          <SummaryValue $color="#d97706">{summary.processing}</SummaryValue>
          <SummarySub>With supplier</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <span className="top red" />
          <SummaryLabel>Disputed</SummaryLabel>
          <SummaryValue $color="#dc2626">{summary.disputed}</SummaryValue>
          <SummarySub>Needs attention</SummarySub>
        </SummaryCard>
      </SummaryBar>

      <Toolbar>
        <ToolbarLeft>
          <SearchWrap>
            <SearchInput
              placeholder="Search order ID or product name..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </SearchWrap>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
            }}
          >
            <option value="">All Statuses</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="transit">In Transit</option>
            <option value="processing">Processing</option>
            <option value="payment_pending">Payment Pending</option>
            <option value="disputed">Disputed</option>
          </FilterSelect>
          <FilterSelect
            value={marketplaceFilter}
            onChange={(e) => {
              setMarketplaceFilter(e.target.value);
            }}
          >
            <option value="">All Marketplaces</option>
            <option value="shopify">Shopify</option>
            <option value="ebay">eBay</option>
            <option value="amazon">Amazon</option>
            <option value="tiktokshop">TikTok Shop</option>
            <option value="walmart">Walmart</option>
          </FilterSelect>
          <FilterSelect
            value={periodFilter}
            onChange={(e) => {
              setPeriodFilter(e.target.value);
            }}
          >
            <option value="30d">Last 30 days</option>
            <option value="3m">Last 3 months</option>
            <option value="6m">Last 6 months</option>
            <option value="all">All time</option>
          </FilterSelect>
        </ToolbarLeft>
        <ToolbarRight>
          <BulkActions $visible={selectedIds.length > 0}>
            <span>{selectedIds.length} selected:</span>
            <BulkBtn className="primary" type="button">Reorder</BulkBtn>
            <BulkBtn className="outline" type="button">Dispute</BulkBtn>
            <BulkBtn className="danger" type="button">Cancel</BulkBtn>
          </BulkActions>
          <ActionBtn type="button" onClick={handleExportCsv} disabled={loading}>
            Export CSV
          </ActionBtn>
        </ToolbarRight>
      </Toolbar>

      <TableContainer>
        {mappedOrders.length > 0 ? (
          <>
            <TableRoot>
              <thead>
                <tr>
                  <th>
                    <Checkbox
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someVisibleSelected;
                      }}
                      onChange={(e) => toggleAllCurrentPage(e.target.checked)}
                    />
                  </th>
                  <th>Product</th>
                  <th>Marketplace</th>
                  <th>Created at</th>
                  <th>Supplier</th>
                  <th>Tracking</th>
                  <th>Status</th>
                  <th>Qty</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {mappedOrders.map((order) => (
                  <tr
                    key={order.id}
                    className={selectedIds.includes(order.id) ? "selected" : ""}
                  >
                    <td>
                      <Checkbox
                        type="checkbox"
                        checked={selectedIds.includes(order.id)}
                        onChange={(e) => toggleOne(order.id, e.target.checked)}
                      />
                    </td>
                    <td>
                      <ProductCell>
                        <ProductThumb $img={order.productImage}>
                          {!order.productImage ? "📦" : ""}
                        </ProductThumb>
                        <div>
                          <ProductName>{order.productName}</ProductName>
                          <ProductId>#{order.productId}</ProductId>
                        </div>
                      </ProductCell>
                    </td>
                    <td>
                      <MarketplaceBadge>{order.marketplace}</MarketplaceBadge>
                    </td>
                    <td>{new Date(order.created).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td>
                      <SupplierCell>
                        <SupplierName>Supplier</SupplierName>
                      </SupplierCell>
                    </td>
                    <td>
                      <TrackingCell>
                        {order.carrierTrackingNumber ? (
                          <>
                            {order.carrier && (
                              <TrackingNum style={{ fontWeight: 600, marginBottom: 2 }}>
                                {order.carrier}
                              </TrackingNum>
                            )}
                            <TrackingNum
                              title="Click to copy"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                navigator.clipboard?.writeText(order.carrierTrackingNumber);
                                message.success("Tracking number copied");
                              }}
                            >
                              {order.carrierTrackingNumber}
                            </TrackingNum>
                            {order.trackingUrl && (
                              <a
                                href={order.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "inline-block",
                                  marginTop: 4,
                                  color: "#1d4ed8",
                                  fontSize: 12,
                                  fontWeight: 500,
                                  textDecoration: "none",
                                }}
                              >
                                Track package
                              </a>
                            )}
                          </>
                        ) : (
                          <TrackingNum>Awaiting tracking...</TrackingNum>
                        )}
                        <div>
                          <TrackStep $active />
                          <TrackStep $active={order.statusTheme.key === "processing" || order.statusTheme.key === "transit" || Boolean(order.carrierTrackingNumber)} />
                          <TrackStep $active={order.statusTheme.key === "fulfilled"} />
                        </div>
                      </TrackingCell>
                    </td>
                    <td>
                      <StatusBadge $bg={order.statusTheme.bg} $color={order.statusTheme.color}>
                        <StatusDot $dot={order.statusTheme.dot} />
                        {getStatusLabel(order.status)}
                      </StatusBadge>
                    </td>
                    <td>{order.quantity}</td>
                    <td>
                      <AmountCell>${order.amount.toFixed(2)}</AmountCell>
                    </td>
                    <td>
                      <ActionGroup>
                        {order.status === "PAYMENT_PENDING" ? (
                          <ActionBtn
                            type="button"
                            className="primary"
                            onClick={() => handlePayOrder(order)}
                          >
                            Pay Now
                          </ActionBtn>
                        ) : (
                          <ActionBtn
                            type="button"
                            onClick={() => navigate(`/my-orders/${order.id}`)}
                          >
                            View
                          </ActionBtn>
                        )}
                        {(order.status === "PENDING" ||
                          order.status === "PAYMENT_PENDING") && (
                            <ActionBtn
                              type="button"
                              className="danger"
                              onClick={() => handleCancelOrder(order.id, order.status)}
                            >
                              Cancel
                            </ActionBtn>
                          )}
                      </ActionGroup>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableRoot>

            <PaginationWrap>
              <PageInfo>
                Showing {startItem}–{endItem} of {totalCount} orders
              </PageInfo>
              <PageButtons>
                <PageButton
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  ‹
                </PageButton>
                {paginationPages.map((pageNum) => (
                  <PageButton
                    key={pageNum}
                    type="button"
                    $active={pageNum === currentPage}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </PageButton>
                ))}
                <PageButton
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  ›
                </PageButton>
              </PageButtons>
            </PaginationWrap>
          </>
        ) : (
          <EmptyState>
            <EmptyIcon>📦</EmptyIcon>
            <EmptyTitle>No orders found</EmptyTitle>
            <EmptySub>
              No orders match your current filters. Try adjusting your search or filter criteria.
            </EmptySub>
            <EmptyCta
              type="button"
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
                setStatusFilter("");
                setMarketplaceFilter("");
                setPeriodFilter("30d");
                setCurrentPage(1);
              }}
            >
              Clear Filters
            </EmptyCta>
          </EmptyState>
        )}
      </TableContainer>
      <Loader isLoading={loading || isUpdating} />
    </MainContainer>
  );
};

export default OrdersDetail;
